import { t } from "@lingui/macro";
import { useEffect, useState } from "react";
import {
  Badge,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@reactive-resume/ui";
import {
  BellIcon,
  CaretCircleLeftIcon,
  CaretCircleRightIcon,
  ExportIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  WalletIcon,
  CheckCircleIcon,
  ClockIcon,
  UsersIcon,
} from "@phosphor-icons/react";
import { useWithdrawals } from "@/client/services/withdrawals/withdrawals";
import { useFilterWithdrawal } from "@/client/stores/filter";
import { useDialog } from "@/client/stores/dialog";
import { useExportWithdrawal } from "@/client/services/withdrawals/export-withdrawal";
import qs from "qs";
import { useSearchParams } from "react-router";
import { Column, DataTable } from "../_components/datatables";
import { CardContent, CardRoot, TitleCard } from "../_components/card";

export const WithdrawAdminPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<"all" | "pending" | "accepted" | "rejected">("all");

  const { filter, replaceFilter, resetFilter } = useFilterWithdrawal();
  const { withdrawals, error, loading } = useWithdrawals(page, filter);
  const { refetch } = useExportWithdrawal(qs.stringify(filter));
  const { open } = useDialog("filter-withdrawal");
  const { open: openGetWithdrawal } = useDialog<{ id: string }>("get-withdrawal");

  useEffect(() => {
    const params = qs.stringify({ page, ...filter });
    setSearchParams(new URLSearchParams(params));
  }, [filter, withdrawals, page, setSearchParams]);

  const handleBackpage = () => setPage(page - 1);
  const handleForwardpage = () => setPage(page + 1);
  const handleSpecficpage = (newpage: number) => setPage(newpage);

  const applyQuickFilters = () => {
    const nextFilter = { ...filter };
    delete nextFilter.userId;
    delete nextFilter.status;

    if (search.trim()) {
      nextFilter.userId = { contains: search.trim() };
    }
    if (status !== "all") {
      nextFilter.status = { equals: status };
    }

    setPage(1);
    replaceFilter(nextFilter);
  };

  const resetFilters = () => {
    setSearch("");
    setStatus("all");
    setPage(1);
    resetFilter();
  };

  // Fonctions de formatage et de badge (reprises de WithdrawalTable)
  const formatDate = (date?: string | Date) => {
    if (!date) return "Non définie";
    return new Date(date).toLocaleDateString();
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "accepted":
        return <Badge variant="success">Accepté</Badge>;
      case "rejected":
        return <Badge variant="error">Rejeté</Badge>;
      case "pending":
        return <Badge variant="warning">En attente</Badge>;
      default:
        return (
          <Badge className="border-0 bg-admin-surface-hover text-admin-text-secondary">
            {status}
          </Badge>
        );
    }
  };

  // Colonnes basées EXACTEMENT sur WithdrawalTable
  const columns: Column<any>[] = [
    {
      key: "reference",
      header: "Reference",
      render: (item) => (
        <span className="font-medium text-admin-text-primary">{item.reference}</span>
      ),
    },
    {
      key: "amount",
      header: t`Montant`,
      render: (item) => <span className="text-admin-text-secondary">{item.amount}</span>,
    },
    {
      key: "demandDate",
      header: "Request Date",
      render: (item) => (
        <span className="text-admin-text-secondary">{formatDate(item.demandDate)}</span>
      ),
    },
    {
      key: "decisionDate",
      header: "Decision Date",
      render: (item) => (
        <span className="text-admin-text-secondary">{formatDate(item.decisionDate)}</span>
      ),
    },
    {
      key: "user",
      header: "Ambassador",
      render: (item) => <span className="text-admin-text-secondary">{item.user?.name || "/"}</span>,
    },
    {
      key: "status",
      header: t`Statut`,
      render: (item) => getStatusBadge(item.status),
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      render: (item) => (
        <Button
          variant="ghost"
          size="icon"
          aria-label="View withdrawal"
          className="text-admin-text-secondary hover:text-admin-text-primary"
          onClick={() => openGetWithdrawal("read", { id: "get-withdrawal", item: { id: item.id } })}
        >
          <EyeIcon size={18} />
        </Button>
      ),
    },
  ];

  return (
    <>
      {/* Header de page */}
      <div className="mb-8 flex flex-col items-start justify-between gap-4 lg:flex-row lg:items-center">
        <div>
          <h1 className="text-3xl font-bold text-admin-text-primary">{t`Retraits`}</h1>
          <p className="mt-1 text-sm text-admin-text-muted">{t`Tableau de bord / Retraits`}</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="relative hidden md:block">
            <MagnifyingGlassIcon
              className="absolute left-3 top-1/2 -translate-y-1/2 text-admin-text-muted"
              size={18}
            />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              type="text"
              placeholder={t`Rechercher des retraits…`}
              className="h-10 rounded-full bg-admin-surface pl-10 pr-4 text-admin-text-primary"
            />
          </div>
          <Button
            variant="outline"
            size="icon"
            aria-label="Notifications"
            className="rounded-full bg-admin-surface text-admin-text-secondary hover:text-admin-text-primary"
          >
            <BellIcon size={18} />
          </Button>
          <div className="h-8 w-8 overflow-hidden rounded-full bg-admin-primary">
            <img src="/avatar.jpg" alt={t`Utilisateur`} className="h-full w-full object-cover" />
          </div>
        </div>
      </div>

      {/* Cartes de Statistiques */}
      <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <CardRoot>
          <CardContent>
            <TitleCard
              text="Demandes affichées"
              number={withdrawals?.data?.length.toString() ?? "0"}
              classNameText=""
              classNameNumber=""
            />
            <div className="rounded-full bg-admin-surface-elevated p-3 text-admin-text-secondary">
              <WalletIcon size={24} weight="duotone" />
            </div>
          </CardContent>
          <div className="px-5 pb-5">
            <span className="text-xs text-admin-success">▲ 4.2%</span>{" "}
            <span className="text-xs text-admin-text-muted">{t`par rapport au mois dernier`}</span>
          </div>
        </CardRoot>

        <CardRoot>
          <CardContent>
            <TitleCard
              text="Acceptées"
              number={
                withdrawals?.data?.filter((w) => w.status === "accepted").length.toString() ?? "0"
              }
              classNameText=""
              classNameNumber=""
            />
            <div className="rounded-full bg-admin-success/20 p-3 text-admin-success">
              <CheckCircleIcon size={24} weight="duotone" />
            </div>
          </CardContent>
          <div className="px-5 pb-5">
            <span className="text-xs text-admin-success">▲ 3.7%</span>{" "}
            <span className="text-xs text-admin-text-muted">{t`par rapport au mois dernier`}</span>
          </div>
        </CardRoot>

        <CardRoot>
          <CardContent>
            <TitleCard
              text="En attente"
              number={
                withdrawals?.data?.filter((w) => w.status === "pending").length.toString() ?? "0"
              }
              classNameText=""
              classNameNumber=""
            />
            <div className="bg-admin-warning/20 text-admin-warning rounded-full p-3">
              <ClockIcon size={24} weight="duotone" />
            </div>
          </CardContent>
          <div className="px-5 pb-5">
            <span className="text-admin-warning text-xs">● Live</span>{" "}
            <span className="text-xs text-admin-text-muted">{t`mis à jour`}</span>
          </div>
        </CardRoot>

        <CardRoot>
          <CardContent>
            <TitleCard
              text="Pages totales"
              number={withdrawals?.page?.toString() ?? "0"}
              classNameText=""
              classNameNumber=""
            />
            <div className="rounded-full bg-admin-accent/20 p-3 text-admin-accent">
              <UsersIcon size={24} weight="duotone" />
            </div>
          </CardContent>
          <div className="px-5 pb-5">
            <span className="text-xs text-admin-accent">▲ 1.3%</span>{" "}
            <span className="text-xs text-admin-text-muted">{t`par rapport au mois dernier`}</span>
          </div>
        </CardRoot>
      </div>

      {/* Section Filtres et Actions */}
      <div className="mb-8 rounded-xl-admin border border-admin-border bg-admin-surface p-6">
        <div className="flex flex-col flex-wrap justify-between gap-4 lg:flex-row">
          {/* Recherche */}
          <div className="relative min-w-[250px] flex-1">
            <MagnifyingGlassIcon
              className="absolute left-3 top-1/2 -translate-y-1/2 text-admin-text-muted"
              size={18}
            />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              type="text"
              placeholder={t`Rechercher par référence ou utilisateur…`}
              className="h-10 w-full bg-admin-background pl-10 text-admin-text-primary"
            />
          </div>

          {/* Filtres Selects */}
          <div className="flex flex-wrap gap-4">
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium text-admin-text-muted">Statut</span>
              <Select value={status} onValueChange={(value) => setStatus(value as typeof status)}>
                <SelectTrigger className="h-10 min-w-32 bg-admin-background text-admin-text-secondary">
                  <SelectValue placeholder={t`Tous les statuts`} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t`Tous les statuts`}</SelectItem>
                  <SelectItem value="pending">En attente</SelectItem>
                  <SelectItem value="accepted">Acceptée</SelectItem>
                  <SelectItem value="rejected">Rejetée</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-end gap-3">
              <Button
                variant="outline"
                onClick={applyQuickFilters}
                className="h-10 border-admin-border bg-admin-background text-admin-text-secondary hover:bg-admin-surface-hover hover:text-admin-text-primary"
              >
                <FunnelIcon size={16} className="mr-2" /> Filter
              </Button>
              <Button
                variant="outline"
                onClick={() => open("create", { id: "filter-withdrawal" })}
                className="h-10 border-admin-border bg-admin-background text-admin-text-secondary hover:bg-admin-surface-hover hover:text-admin-text-primary"
              >
                <FunnelIcon size={16} className="mr-2" /> Advanced filters
              </Button>
              <Button
                variant="ghost"
                onClick={resetFilters}
                className="h-10 text-admin-text-muted hover:text-admin-text-primary"
              >
                Reset
              </Button>
              <Button
                variant="outline"
                onClick={() => refetch()}
                className="h-10 bg-admin-background text-admin-text-secondary hover:text-admin-text-primary"
              >
                <ExportIcon size={16} className="mr-2" /> Export
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Tableau & Pagination */}
      <DataTable
        title=""
        data={withdrawals?.data ?? []}
        columns={columns}
        pageSize={10}
        className="shadow-sm-admin"
      />

      {/* Pagination */}
      <div className="my-3 flex flex-row justify-end space-x-3">
        {page > 1 && (
          <Button variant="outline" onClick={handleBackpage} className="rounded-lg">
            <CaretCircleLeftIcon size={32} weight="regular" />
          </Button>
        )}
        {page - 2 > 0 && (
          <Button
            variant="outline"
            onClick={() => handleSpecficpage(page - 2)}
            className="rounded-lg"
          >
            {page - 2}
          </Button>
        )}
        {page - 1 > 0 && (
          <Button
            variant="outline"
            onClick={() => handleSpecficpage(page - 1)}
            className="rounded-lg"
          >
            {page - 1}
          </Button>
        )}

        <Button variant="secondary" className="bg-admin-primary text-admin-primary-foreground">
          {page}
        </Button>
        {withdrawals && page + 1 <= withdrawals.page && (
          <Button
            variant="outline"
            onClick={() => handleSpecficpage(page + 1)}
            className="rounded-lg"
          >
            {page + 1}
          </Button>
        )}
        {withdrawals && page + 2 <= withdrawals.page && (
          <Button
            variant="outline"
            onClick={() => handleSpecficpage(page + 2)}
            className="rounded-lg"
          >
            {page + 2}
          </Button>
        )}
        {withdrawals && page < withdrawals.page && (
          <Button variant="outline" onClick={handleForwardpage} className="rounded-lg">
            <CaretCircleRightIcon size={32} weight="regular" />
          </Button>
        )}
      </div>
    </>
  );
};
